// Wave UX-2.2 #7 (2026-05-23) — 외부 바이어 마이페이지.
// 프로필 + 매너 + 활동 진입 + 설정 + 로그아웃.
// 데이터: sessionStorage.autodex_buyer + GET /buyer-app/trust-speed/me (신뢰속도 카드 + 매입 완료).
//   매너 이벤트 이력은 /me/manner-history 유지 (interim — 매너 backend 는 TS-1.g 까지 존속).

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../utils/api';
import { authBuyerFetch, isUnauthorized, handleUnauthorized } from './buyerApi';
import AuctionBuyerLayout from './AuctionBuyerLayout';
import LanguageSwitcher from '../../i18n/LanguageSwitcher';
import { useT } from '../../i18n/I18nProvider';
import { useDeviceConfig } from './deviceConfig';
import { confirm } from './ConfirmDialog';
import { getGrade, gradeI18nKey } from '../../utils/trustSpeedGrades';

// 신뢰속도 카드 — 당근마켓 매너온도(°C) 대체. 등급 + 속도(km/h) + 매입 완료 건수.
//   매입 완료(completedDeals) = "우량 바이어" 신호 (금액 미노출).
function TrustSpeedCardCompact(props) {
  var t = props.t;
  var s = props.speed;
  var grade = (s && s.grade) || 'IDLE';
  var g = getGrade(grade);
  var speedVal = s ? Math.round(Number(s.trustSpeed) || 0) : 0;
  var completed = s ? (s.completedDeals || 0) : 0;
  return (
    <div style={{
      background: 'linear-gradient(135deg, ' + g.color + ' 0%, #1B3A5C 100%)',
      color: 'white', borderRadius: 14,
      padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      marginBottom: 16,
    }}>
      <div>
        <div style={{ fontSize: 11, fontWeight: 600, opacity: 0.85 }}>{t('speedCardTitle')}</div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6, fontSize: 15, fontWeight: 800 }}>
          <span style={{ fontSize: 17 }}>{g.emoji}</span>{t(gradeI18nKey(grade))}
        </div>
        <div style={{ marginTop: 8, display: 'inline-block', background: 'rgba(255,255,255,0.18)', padding: '4px 12px', borderRadius: 999, fontSize: 12, fontWeight: 800 }}>
          {t('purchaseCompletedLabel')} {completed}
        </div>
      </div>
      <div style={{ textAlign: 'right' }} dir="ltr">
        <div style={{ fontSize: 28, fontWeight: 900, lineHeight: 1 }}>
          {speedVal}<span style={{ fontSize: 14, fontWeight: 700, marginLeft: 3 }}>{t('speedCardUnit')}</span>
        </div>
      </div>
    </div>
  );
}

function Row(props) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '12px 16px',
      borderBottom: props.last ? 'none' : '1px solid #f1f5f9',
      cursor: props.onClick ? 'pointer' : 'default',
    }} onClick={props.onClick}>
      <span style={{ fontSize: 13, color: '#475569' }}>{props.label}</span>
      <span style={{ fontSize: 13, color: props.muted ? '#94a3b8' : '#0f172a', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
        {props.value}
        {props.onClick && <span style={{ color: '#cbd5e1' }}>›</span>}
      </span>
    </div>
  );
}

function Section(props) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 11, fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8, padding: '0 4px' }}>
        {props.title}
      </div>
      <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {props.children}
      </div>
    </div>
  );
}

// Wave UX-2.4 #25 — reason 키 매핑 (rfq_fail_reward_* 처럼 suffix 가 붙는 경우 prefix 매칭)
function mannerReasonKey(reason) {
  var r = String(reason || '').toLowerCase();
  if (r.indexOf('rfq_commit_reward') === 0) return 'mannerHistoryReason_rfq_commit_reward';
  if (r.indexOf('rfq_complete_reward') === 0) return 'mannerHistoryReason_rfq_complete_reward';
  if (r.indexOf('rfq_fail_reward') === 0) return 'mannerHistoryReason_rfq_fail_reward';
  if (r === 'd15_penalty' || r.indexOf('d15_penalty') === 0) return 'mannerHistoryReason_d15_penalty';
  if (r === 'd30_penalty' || r.indexOf('d30_penalty') === 0) return 'mannerHistoryReason_d30_penalty';
  if (r === 'd15_recovery' || r.indexOf('d15_recovery') === 0) return 'mannerHistoryReason_d15_recovery';
  if (r === 'd30_recovery' || r.indexOf('d30_recovery') === 0) return 'mannerHistoryReason_d30_recovery';
  if (r.indexOf('admin') === 0 || r.indexOf('admin_') === 0) return 'mannerHistoryReason_admin';
  return 'mannerHistoryReason_other';
}

function MannerHistorySection(props) {
  var t = props.t;
  var events = props.events || [];
  if (!events.length) {
    return (
      <Section title={t('mannerHistoryTitle')}>
        <div style={{ padding: '20px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
          {t('mannerHistoryEmpty')}
        </div>
      </Section>
    );
  }
  return (
    <Section title={t('mannerHistoryTitle')}>
      {events.map(function(ev, i) {
        var positive = ev.delta > 0;
        var sign = positive ? '+' : '';
        var dateStr = String(ev.created_at || '').slice(0, 10);
        return (
          <div key={ev.id || i} style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: i === events.length - 1 ? 'none' : '1px solid #f1f5f9',
          }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: '#0f172a', fontWeight: 600 }}>{t(mannerReasonKey(ev.reason))}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{dateStr}</div>
            </div>
            <div style={{
              fontSize: 14, fontWeight: 800,
              color: positive ? '#16a34a' : '#dc2626',
              minWidth: 50, textAlign: 'right',
            }} dir="ltr">
              {sign}{Number(ev.delta).toFixed(1)}°
            </div>
          </div>
        );
      })}
    </Section>
  );
}

function AuctionBuyerMe() {
  var t = useT();
  var navigate = useNavigate();
  // perf/buyer-iife-lazy (2026-05-24) — useState lazy init (mount 1회) → 매 render IIFE 차단
  var [buyer] = useState(function(){
    try { return JSON.parse(sessionStorage.getItem('autodex_buyer')||'null'); } catch(e) { return null; }
  });
  var [speed, setSpeed] = useState(null);
  var [history, setHistory] = useState([]);
  // Wave UX-2.4 #26 — 1기기 정책 활성 여부 (Me 설정 row 자동 노출)
  var deviceConfig = useDeviceConfig();
  var oneDeviceEnabled = !!(deviceConfig && deviceConfig.oneDeviceEnabled);

  var load = useCallback(function() {
    // 신뢰속도 (당근마켓 매너온도 대체) — 본인 등급/속도/매입 완료 건수
    authBuyerFetch(API + '/buyer-app/trust-speed/me', {})
      .then(function(r){ if (isUnauthorized(r)) throw new Error('unauthorized'); return r.json(); })
      .then(function(d){ if (d && d.success && d.data) setSpeed(d.data); })
      .catch(function(e){ if (e && e.message === 'unauthorized') handleUnauthorized(navigate); });
    // Wave UX-2.4 #25 — manner-history 로딩 (manner 호출과 병렬)
    authBuyerFetch(API + '/buyer-app/me/manner-history?limit=20', {})
      .then(function(r){ if (isUnauthorized(r)) throw new Error('unauthorized'); return r.json(); })
      .then(function(d){ if (d && d.success && d.data) setHistory(d.data.events || []); })
      .catch(function(e){ if (e && e.message === 'unauthorized') handleUnauthorized(navigate); });
  }, [navigate]);

  useEffect(function(){ load(); }, [load]);

  function signOut() {
    // P2 #8 (2026-05-24) — window.confirm → custom ConfirmDialog
    confirm({ message: t('meSignOutConfirm') }).then(function(ok) {
      if (!ok) return;
      localStorage.removeItem('autodex_buyer_token');
      localStorage.removeItem('autodex_buyer_refresh');
      sessionStorage.removeItem('autodex_buyer');
      navigate('/buyer/login');
    });
  }

  var name = buyer && buyer.name ? buyer.name : t('dashboardFallbackName');
  var email = buyer && buyer.email ? buyer.email : '';
  var phone = buyer && (buyer.phone_e164 || buyer.phone) ? (buyer.phone_e164 || buyer.phone) : '';
  var country = buyer && buyer.country ? buyer.country : '';
  var company = buyer && buyer.company ? buyer.company : '';
  var joined = buyer && (buyer.created_at || buyer.joinedAt) ? String(buyer.created_at || buyer.joinedAt).slice(0, 10) : '';

  return (
    <AuctionBuyerLayout activeKey={null}>
      <div style={{ maxWidth: 720, margin: '0 auto', padding: '24px 24px 80px', }}>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0f172a', marginBottom: 16 }}>{t('meTitle')}</h2>

        {/* 신뢰속도 카드 (매너온도 대체) */}
        <TrustSpeedCardCompact t={t} speed={speed} />

        {/* Wave UX-2.4 #25 — 매너 이벤트 history */}
        <MannerHistorySection t={t} events={history} />

        {/* 프로필 */}
        <Section title={t('meProfileSection')}>
          <Row label={t('registerNameLabel').replace(' *', '')} value={name} />
          <Row label={t('meFieldEmail')} value={email || t('meUnset')} muted={!email} />
          <Row label={t('meFieldPhone')} value={phone || t('meUnset')} muted={!phone} />
          <Row label={t('meFieldCountry')} value={country || t('meUnset')} muted={!country} />
          {company && <Row label={t('meFieldCompany')} value={company} />}
          {joined && <Row label={t('meFieldJoined')} value={joined} last />}
        </Section>

        {/* 활동 */}
        <Section title={t('meActivitySection')}>
          <Row label={t('meActivityBids')} value="" onClick={function(){ navigate('/buyer/bids'); }} />
          <Row label={t('meActivityFavorites')} value="" onClick={function(){ navigate('/buyer/favorites'); }} />
          <Row label={t('notifCenterTitle')} value="" onClick={function(){ navigate('/buyer/me/notifications'); }} last />
        </Section>

        {/* 설정 */}
        <Section title={t('meSettingsSection')}>
          <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, color: '#475569' }}>{t('meSettingsLanguage')}</span>
            <LanguageSwitcher />
          </div>
          {/* Wave UX-2.4 #26 — 1기기 정책 활성 시 정책 상태 row */}
          {oneDeviceEnabled && (
            <Row label={t('devicePolicyMeRowLabel')} value={t('devicePolicyMeRowValue')} last />
          )}
        </Section>

        {/* 계정 */}
        <Section title={t('meDangerSection')}>
          <Row label={t('meSignOut')} value=""
            onClick={signOut}
            muted />
        </Section>
      </div>
    </AuctionBuyerLayout>
  );
}

export default AuctionBuyerMe;
